import { useState, useEffect } from 'react';
import { Card, Button, Badge } from '@atoms/index';
import { useRestaurantStore } from '@store/restaurantStore';
import { restaurantService } from '@services/restaurantService';
import { RestaurantStatusSelector } from '@organisms/RestaurantStatusSelector';
import { useRestaurantSocket } from '@/hooks/useRestaurantSocket';
import { formatPrice } from '@/utils/format';
import { formatCurrency } from '@/utils/currency';
import { menuService } from '@services/menuService';

/**
 * DashboardTab - Restaurant dashboard overview
 *
 * Per Restaurant Management Panel: Dashboard tab
 * Features:
 * - Stats cards: Today's revenue, orders, average rating
 * - Recent orders list (last 10)
 * - Quick actions: Toggle accepting orders, view menu, add product
 * - Real-time updates via WebSocket (future)
 */
const DashboardTab = () => {
  const {
    restaurant,
    stats,
    orders,
    isLoadingStats,
    loadStats,
    loadOrders,
    loadRestaurant,
  } = useRestaurantStore();

  const [recentOrders, setRecentOrders] = useState<any[]>([]);
  const [liveAnalytics, setLiveAnalytics] = useState<any>(null);
  const [isLoadingLive, setIsLoadingLive] = useState(false);
  const [lowStockProducts, setLowStockProducts] = useState<any[]>([]);

  // Load stats and recent orders on mount
  useEffect(() => {
    loadStats();
    loadOrders({ limit: 10 });
    loadLiveAnalytics();
    loadLowStockProducts();
    const interval = setInterval(() => {
      loadLiveAnalytics();
      loadLowStockProducts();
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadLowStockProducts = async () => {
    try {
      const products = await menuService.getLowStockProducts();
      setLowStockProducts(products);
    } catch (error) {
      console.error('Failed to load low stock products:', error);
    }
  };

  // Update recent orders when orders change
  useEffect(() => {
    setRecentOrders(orders.slice(0, 10));
  }, [orders]);

  const loadLiveAnalytics = async () => {
    if (!restaurant?.id) return;
    setIsLoadingLive(true);
    try {
      const analytics = await restaurantService.getLiveAnalytics();
      setLiveAnalytics(analytics);
    } catch (error) {
      console.error('Failed to load live analytics:', error);
    } finally {
      setIsLoadingLive(false);
    }
  };

  const handleStatusChange = async (
    status: 'ONLINE' | 'OFFLINE' | 'BUSY' | 'SUSPENDED',
    duration?: number,
  ) => {
    if (!restaurant) return;
    try {
      await restaurantService.updateStatus(status, duration);
      await loadRestaurant();
      await loadLiveAnalytics();
    } catch (error) {
      console.error('Failed to update status:', error);
      throw error;
    }
  };

  useRestaurantSocket({
    restaurantId: restaurant?.id || '',
    role: 'admin',
    onStatusChange: () => {
      loadRestaurant();
      loadLiveAnalytics();
    },
  });

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <div className="text-center">
            <p className="text-3xl font-bold text-primary">
              {stats?.totalRevenue ? formatPrice(stats.totalRevenue) : '₺0'}
            </p>
            <p className="text-sm text-gray-600">Bugünkü Gelir</p>
          </div>
        </Card>

        <Card>
          <div className="text-center">
            <p className="text-3xl font-bold text-warning">
              {stats?.totalOrders || 0}
            </p>
            <p className="text-sm text-gray-600">Bugünkü Siparişler</p>
          </div>
        </Card>

        <Card>
          <div className="text-center">
            <p className="text-3xl font-bold text-success">
              {restaurant?.rating
                ? typeof restaurant.rating === 'number'
                  ? restaurant.rating.toFixed(1)
                  : parseFloat(String(restaurant.rating || '0')).toFixed(1)
                : '0.0'}
            </p>
            <p className="text-sm text-gray-600">Ortalama Puan</p>
          </div>
        </Card>

        <Card>
          <div className="text-center">
            <p className="text-3xl font-bold text-info">
              {restaurant?.reviewCount || 0}
            </p>
            <p className="text-sm text-gray-600">Değerlendirme</p>
          </div>
        </Card>
      </div>

      {/* Live Analytics */}
      {liveAnalytics && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <div className="text-center">
              <p className="text-2xl font-bold text-warning">
                {liveAnalytics.ordersInQueue}
              </p>
              <p className="text-sm text-gray-600">Sıradaki Siparişler</p>
            </div>
          </Card>
          <Card>
            <div className="text-center">
              <p className="text-2xl font-bold text-success">
                {liveAnalytics.ordersReady}
              </p>
              <p className="text-sm text-gray-600">Hazır Siparişler</p>
            </div>
          </Card>
          <Card>
            <div className="text-center">
              <p className="text-2xl font-bold text-info">
                {liveAnalytics.nextOrderReadyIn !== null
                  ? `${liveAnalytics.nextOrderReadyIn} dk`
                  : '-'}
              </p>
              <p className="text-sm text-gray-600">Sonraki Sipariş Hazır</p>
            </div>
          </Card>
          <Card>
            <div className="text-center">
              <p className="text-2xl font-bold text-primary">
                {liveAnalytics.courierEstimatedArrival !== null
                  ? `${liveAnalytics.courierEstimatedArrival} dk`
                  : '-'}
              </p>
              <p className="text-sm text-gray-600">Kurye Tahmini Varış</p>
            </div>
          </Card>
        </div>
      )}

      {/* Status Management */}
      {restaurant && (
        <Card>
          <h3 className="text-lg font-bold text-gray-900 mb-4">Restoran Durumu</h3>
          <RestaurantStatusSelector
            currentStatus={(restaurant as any).status || 'OFFLINE'}
            onStatusChange={handleStatusChange}
          />
        </Card>
      )}

      {/* Low Stock Alert */}
      {lowStockProducts.length > 0 && (
        <Card className="bg-warning/10 border-warning/20">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-lg font-bold text-warning">⚠️ Düşük Stok Uyarısı</h3>
            <Badge variant="warning">{lowStockProducts.length} Ürün</Badge>
          </div>
          <div className="space-y-2">
            {lowStockProducts.slice(0, 5).map((product) => (
              <div key={product.id} className="flex items-center justify-between text-sm">
                <span className="text-gray-700">{product.name}</span>
                <span className="font-bold text-warning">
                  {product.stockQuantity} adet (Eşik: {product.lowStockThreshold || 10})
                </span>
              </div>
            ))}
            {lowStockProducts.length > 5 && (
              <p className="text-xs text-gray-600 mt-2">
                +{lowStockProducts.length - 5} ürün daha...
              </p>
            )}
          </div>
        </Card>
      )}

      {/* Recent Orders */}
      <Card>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-gray-900">Son Siparişler</h3>
          <Button
            size="sm"
            variant="primary"
            onClick={() => {
              // Navigate to orders tab (would need router or state management)
            }}
          >
            Tümünü Gör
          </Button>
        </div>

        {recentOrders.length === 0 ? (
          <div className="text-center py-8 text-gray-400">
            <p>Henüz sipariş yok</p>
          </div>
        ) : (
          <div className="space-y-3">
            {recentOrders.map((order) => (
              <div
                key={order.id}
                className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <div>
                  <p className="font-medium text-gray-900">
                    Sipariş #{order.orderNumber || order.id.slice(0, 8)}
                  </p>
                  <p className="text-sm text-gray-600">
                    {new Date(order.createdAt).toLocaleString('tr-TR')}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <Badge
                    variant={
                      order.status === 'CREATED'
                        ? 'info'
                        : order.status === 'PREPARING'
                        ? 'warning'
                        : 'success'
                    }
                  >
                    {order.status}
                  </Badge>
                  <span className="font-bold text-gray-900">
                    {formatCurrency(
                      typeof order.total === 'number' && !isNaN(order.total)
                        ? order.total
                        : (order.subtotal || 0) + (order.deliveryFee || 0) + (order.serviceFee || 0) || 0
                    )}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
};

export default DashboardTab;
